// @flow

import styled from 'styled-components';
import * as React from 'react';
import {
  Button,
  Icon,
  InputGroup,
  Tooltip,
  Popover,
  TextArea,
} from '@blueprintjs/core';
import { PanelHeader } from '../../panel';
import { breakpoints } from '../../../styles/variables';

// Locally compact version of PanelHeader to reduce TopBar height only
const CompactPanelHeader = styled(PanelHeader).withConfig({
  componentId: 'ai-reports-topbar-compact',
})`
  padding: 0.75rem 1rem; /* override default 2rem to make header shorter */
  /* Round bottom corners to match the top, even when header isn't the last child */
  border-bottom-left-radius: var(--widget-border-radius);
  border-bottom-right-radius: var(--widget-border-radius);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  font-family: var(--fonts-heading);
  color: var(--widget-heading-text);
`;

const TitleIcon = styled(Icon)`
  margin-right: 0.4rem;
  color: var(--widget-heading-text);
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  min-width: 0; /* allow children to shrink instead of overflowing */
  flex: 1 1 auto;
  flex-wrap: wrap; /* allow controls to wrap on smaller screens */

  @media (max-width: ${breakpoints.TABLET_SIZE}) {
    flex-basis: 100%; /* move actions below title on small screens */
  }
`;

const LeftStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0; /* allow inner text to ellipsis if needed */
  flex: 0 1 auto;

  @media (max-width: ${breakpoints.TABLET_SIZE}) {
    flex-basis: 100%; /* take full row when wrapping */
  }
`;

const InputsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1 1 100%; /* inputs area takes full width line inside actions */
  flex-wrap: wrap; /* wrap name/description when constrained */
`;

const NameBox = styled.div`
  flex: 1 1 auto;
  min-width: 15ch;
  max-width: 50ch;
`;

const DescriptionBox = styled.div`
  flex: 2 1 auto;
  min-width: 20ch;
  max-width: 200ch;
`;

const ShrinkInput = styled(InputGroup)`
  width: 100%;
  .bp3-input {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .bp3-input:focus {
    text-overflow: clip; /* show full content while editing */
    overflow: auto; /* allow horizontal scroll if long */
  }
`;

// Match TextArea typography to single-line inputs
const MatchInputTextArea = styled(TextArea)`
  font: inherit;
  font-family: inherit;
  color: inherit;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  flex-wrap: wrap; /* allow title/actions to stack on small screens */
`;

const TopBar = (props) => {
  const {
    onReset,
    disabled,
    inEditMode,
    resetEnabled,
    name,
    onNameChange,
    description,
    onDescriptionChange,
    inputsEnabled,
    onSave,
    saveDisabled,
    onCancel,
    onSaveAsNew,
    ui,
  } = props;

  const copy = (ui && ui.copy && ui.copy.topBar) || {};
  const limits = (ui && ui.limits) || {};
  const nameMaxLen = limits.reportNameMaxLen || 50;
  const descMaxLen = limits.reportDescriptionMaxLen || 200;

  const clampLen = (value, maxLen) => String(value || '').slice(0, maxLen);

  const [isDescExpanded, setDescExpanded] = React.useState(false);

  return (
    <>
      <CompactPanelHeader>
        <HeaderRow>
          <LeftStack>
            <Title>
              <TitleIcon icon="draw" size={18} />
              {copy.title || 'Ask Aura Reports'}
            </Title>
          </LeftStack>
          <Actions>
            <InputsRow>
              <NameBox>
                <ShrinkInput
                  placeholder={copy.namePlaceholder || 'Report name'}
                  value={name || ''}
                  title={name || ''}
                  onChange={(e) => {
                    const val = clampLen(e.target.value, nameMaxLen);
                    onNameChange && onNameChange(val);
                  }}
                  disabled={!inputsEnabled}
                />
              </NameBox>
              <DescriptionBox>
                <ShrinkInput
                  placeholder={copy.descriptionPlaceholder || 'Short description'}
                  value={description || ''}
                  title={description || ''}
                  onChange={(e) => {
                    const val = clampLen(e.target.value, descMaxLen);
                    onDescriptionChange && onDescriptionChange(val);
                  }}
                  disabled={!inputsEnabled}
                  rightElement={
                    <Popover
                      isOpen={isDescExpanded}
                      onInteraction={(nextOpen) => setDescExpanded(!!nextOpen)}
                      content={
                        <div style={{ padding: '0.75rem', maxWidth: 520 }}>
                          <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
                            {copy.descriptionPopoverTitle || 'Edit description'}
                          </div>
                          <MatchInputTextArea
                            fill
                            rows={8}
                            placeholder={copy.descriptionPlaceholder || 'Short description'}
                            value={description || ''}
                            onChange={(e) => {
                              const val = clampLen(e.target.value, descMaxLen);
                              onDescriptionChange && onDescriptionChange(val);
                            }}
                            autoFocus
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                            <span style={{ opacity: 0.7, fontSize: 12 }}>
                              {(description || '').length}/{descMaxLen}
                            </span>
                            <Button small intent="primary" onClick={() => setDescExpanded(false)}>
                              {copy.descriptionDoneLabel || 'Done'}
                            </Button>
                          </div>
                        </div>
                      }
                    >
                      <Tooltip content={copy.descriptionExpandTooltip || 'Expand to edit'} position="bottom">
                        <Button
                          minimal
                          icon="edit"
                          onClick={() => inputsEnabled && setDescExpanded(true)}
                          disabled={!inputsEnabled}
                          aria-label="Expand description"
                        />
                      </Tooltip>
                    </Popover>
                  }
                />
              </DescriptionBox>
              <Tooltip
                content={(copy.save && copy.save.tooltip) || 'Save'}
                position="bottom"
              >
                <Button
                  intent="primary"
                  icon="floppy-disk"
                  onClick={onSave}
                  disabled={!!saveDisabled}
                >
                  {(copy.save && copy.save.label) || 'Save'}
                </Button>
              </Tooltip>
              {inEditMode && onSaveAsNew && (
                <Tooltip content={(copy.saveAsNew && copy.saveAsNew.tooltip) || 'Save as new'} position="bottom">
                  <Button
                    intent="primary"
                    icon="plus"
                    onClick={onSaveAsNew}
                    disabled={!!saveDisabled}
                  >
                    {(copy.saveAsNew && copy.saveAsNew.label) || 'New'}
                  </Button>
                </Tooltip>
              )}
              {inEditMode && (
                <Tooltip content={(copy.cancel && copy.cancel.tooltip) || 'Cancel'} position="bottom" openOnTargetFocus={false}>
                  <Button
                    intent="primary"
                    icon="undo"
                    onClick={onCancel}
                    disabled={!!disabled}
                  >
                    {(copy.cancel && copy.cancel.label) || 'Cancel'}
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                content={(copy.reset && copy.reset.tooltip) || 'Reset'}
                position="bottom"
                openOnTargetFocus={false}
              >
                <Button
                  intent="primary"
                  icon="refresh"
                  onClick={onReset}
                  disabled={inEditMode ? !resetEnabled || disabled : disabled}
                >
                  {(copy.reset && copy.reset.label) || (inEditMode ? 'Reset' : 'New')}
                </Button>
              </Tooltip>
            </InputsRow>
          </Actions>
        </HeaderRow>
      </CompactPanelHeader>
    </>
  );
};

export default TopBar;
